import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useFamily } from '../../contexts/FamilyContext';
import MobileDrawer from './MobileDrawer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons';

export default function MobileHeader() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [profileImageError, setProfileImageError] = useState(false);
  const { user, familyId } = useAuth();
  const { family } = useFamily();
  const location = useLocation();
  const navigate = useNavigate();

  // Reset profile image error when user or photoURL changes
  useEffect(() => {
    setProfileImageError(false);
  }, [user?.photoURL]);

  // Close drawer when route changes (only if drawer is open)
  useEffect(() => {
    if (drawerOpen) {
      setDrawerOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Add swipe gesture to open drawer from left edge
  useEffect(() => {
    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartTime = 0;
    
    const handleTouchStart = (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      touchStartTime = Date.now();
    };
    
    const handleTouchMove = (e) => {
      const touchCurrentX = e.touches[0].clientX;
      const touchCurrentY = e.touches[0].clientY;
      const diffX = touchCurrentX - touchStartX;
      const diffY = Math.abs(touchCurrentY - touchStartY);
      const timeDiff = Date.now() - touchStartTime;
      
      // If swipe started from left edge (within 30px) and moved right >50px
      // and it's a horizontal swipe (not vertical) and quick (< 300ms)
      if (touchStartX < 30 && diffX > 50 && diffY < 100 && timeDiff < 300 && !drawerOpen) {
        setDrawerOpen(true);
      }
    };
    
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, [drawerOpen]);

  // Check if we're on the Dashboard (for floating header style)
  const isDashboard = location.pathname === '/';

  // Don't show header on login, onboarding, or public routes
  const hideHeaderPaths = ['/login', '/onboarding', '/accept-invite'];
  if (hideHeaderPaths.some(path => location.pathname.startsWith(path))) {
    return null;
  }

  if (!user || !familyId) {
    return null;
  }

  return (
    <>
      {/* Mobile header - only shows on mobile, fixed to top of viewport */}
      <header 
        className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-border"
        style={{ zIndex: 50 }}
      >
        <div className="flex items-center justify-between px-4 py-3 h-14">
          {/* Hamburger button */}
          <button
            onClick={() => setDrawerOpen(true)}
            className="p-2 hover:bg-black/5 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center active:scale-95 touch-target"
            aria-label="Open menu"
            type="button"
          >
            <svg 
              className="w-6 h-6 text-text-primary" 
              fill="none" 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth="2" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Profile avatar (right side) */}
          <button
            onClick={() => {
              if (isDashboard) {
                // Navigate to settings on Dashboard
                navigate('/settings');
              } else {
                setDrawerOpen(true);
              }
            }}
            className={`flex items-center justify-center w-10 h-10 min-h-[44px] min-w-[44px] rounded-full ${isDashboard ? 'border-2 border-white shadow-lg bg-white/10 backdrop-blur-sm' : 'bg-primary/10'} active:scale-95 transition-transform touch-target`}
            aria-label="Profile"
          >
            {user.photoURL && !profileImageError ? (
              <img 
                src={user.photoURL} 
                alt={user.displayName || 'Profile'} 
                className="w-full h-full rounded-full object-cover"
                onError={() => {
                  console.error('Profile picture failed to load:', user.photoURL);
                  setProfileImageError(true);
                }}
              />
            ) : (
              <div className={`w-full h-full rounded-full ${isDashboard ? 'bg-primary' : 'bg-primary/10'} flex items-center justify-center`}>
                <span className={`${isDashboard ? 'text-white' : 'text-primary'} font-bold text-sm`}>
                  {user?.displayName?.[0] || 'J'}
                </span>
              </div>
            )}
          </button>
        </div>
      </header>

      {/* Mobile Drawer */}
      <MobileDrawer 
        isOpen={drawerOpen} 
        onClose={() => setDrawerOpen(false)} 
      />

      {/* No spacer needed for sticky header - it takes up space naturally */}
    </>
  );
}
